import {
  formatDisplayDate,
  formatMoney,
  formatQty,
  printDisplay,
  type DateDisplay,
} from '@ims/core'
import type { TenantSettingsRow } from '@ims/data'
import { businessDateDisplay } from './calendar.ts'

export function money(
  paisa: number,
  settings?: Pick<TenantSettingsRow, 'currency_symbol'> | null,
): string {
  return formatMoney(paisa, { symbol: settings?.currency_symbol ?? 'Rs.' })
}

export function qty(milli: number): string {
  return formatQty(milli)
}

export function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

type DateSettings =
  | (Pick<TenantSettingsRow, 'date_display'> & { business_date_display?: DateDisplay })
  | null
  | undefined

function displayOf(settings: DateSettings): DateDisplay {
  return settings?.date_display ?? 'both'
}

/** "2025-09-27 · 11 Asoj 2082", "11 Asoj 2082" or "2025-09-27" (Settings → Show dates in). */
export function dateLabel(iso: string | null | undefined, settings?: DateSettings): string {
  if (!iso) return '—'
  return formatDisplayDate(localDay(iso), displayOf(settings))
}

/**
 * A date for a table cell or a tight line. With "Nepali and English" the row shows the
 * Nepali date only; give the cell `title={dateLabel(…)}` so the English one is a hover away.
 * The other two settings read the same as `dateLabel`.
 */
export function dateShort(iso: string | null | undefined, settings?: DateSettings): string {
  if (!iso) return '—'
  const display = displayOf(settings)
  return formatDisplayDate(localDay(iso), display === 'both' ? 'bs' : display)
}

/** A date stays as it is; a timestamp becomes its day in local time. */
export function localDay(iso: string): string {
  if (iso.length <= 10) return iso
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Dates on printed bills and the public page: the Nepali date is always there. */
export function printDateLabel(iso: string | null | undefined, settings?: DateSettings): string {
  if (!iso) return '—'
  // The business's setting, not this device's choice (lib/calendar.ts).
  return formatDisplayDate(localDay(iso), printDisplay(businessDateDisplay(settings)))
}

/** A moment in local time: "11 Asoj 2082, 3:45 PM" (the date follows the setting). */
export function dateTime(iso: string | null | undefined, settings?: DateSettings): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (!settings) return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  return `${dateLabel(iso, settings)}, ${d.toLocaleTimeString(undefined, { timeStyle: 'short' })}`
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`
  return `${Math.floor(seconds / 86400)} d ago`
}
